import Company from '@/App/Company/Entity/Company'
import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
import User from '@/App/User/Entity/User'
import type { TawktoSupportChatInput } from '@/Shared/Hook/useSupportChat'
import type {
    PageMeta,
    FlashStatusType,
    AppSharedDataRawData,
} from '@/Shared/AutoGenerated/Main'
import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'

export default class AppSharedData {
    constructor(
        public readonly appName: string,
        public readonly currentRoute: string,
        public readonly isImpersonating: boolean,
        public readonly authUser: User | null,
        public readonly authCompany: Company | null,
        public readonly authCompanyUser: CompanyUser | null,
        public readonly tawktoScriptUrl: string | null,
        public readonly tawktoUserName: string | null,
        public readonly tawktoUserEmail: string | null,
        public readonly tawktoUserDataHash: string | null,
        public readonly flashStatus: FlashStatusType | null,
        public readonly integrationsRequiringAttention: number,
    ) {
        ensureNoUndefinedProperties(this)
    }

    // ------------------
    // BaseApp
    // ------------------

    static hydrate(rawData: AppSharedDataRawData): AppSharedData {
        return new AppSharedData(
            rawData.appName,
            rawData.currentRoute,
            rawData.isImpersonating,
            User.hydrateSingleOrNull(rawData.authUser),
            Company.hydrateSingleOrNull(rawData.authCompany),
            CompanyUser.hydrateSingleOrNull(rawData.authCompanyUser),
            rawData.tawktoScriptUrl,
            rawData.tawktoUserName,
            rawData.tawktoUserEmail,
            rawData.tawktoUserDataHash,
            rawData.flashStatus,
            rawData.integrationsRequiringAttention,
        )
    }

    getTawktoSupportChatInputs(): TawktoSupportChatInput {
        return {
            tawktoScriptUrl: this.tawktoScriptUrl,
            tawktoUserName: this.tawktoUserName,
            tawktoUserEmail: this.tawktoUserEmail,
            tawktoUserDataHash: this.tawktoUserDataHash,
        }
    }

    getAppSidebarLayoutProps(pageMeta: PageMeta) {
        if (!this.authUser) {
            throw new Error('Auth user is required')
        }

        if (!this.authCompanyUser) {
            throw new Error('Auth company user is required')
        }

        if (!this.authCompany) {
            throw new Error('Auth company is required')
        }

        return {
            pageMeta: pageMeta,
            appName: this.appName,
            authUser: this.authUser,
            authCompanyUser: this.authCompanyUser,
            impersonating: this.isImpersonating,
            currentRoute: this.currentRoute,
            tawktoScriptInputs: this.getTawktoSupportChatInputs(),
            flashStatus: this.flashStatus,
            integrationsRequiringAttention: this.integrationsRequiringAttention,
        }
    }

    getAppTopbarLayoutProps(pageMeta: PageMeta) {
        return {
            ...this.getAppSidebarLayoutProps(pageMeta),
            hideMenus: true,
        }
    }

    getAuthLayoutProps() {
        return {
            appName: this.appName,
            flashStatus: this.flashStatus,
        }
    }

    getAdminLayoutProps(pageMeta: PageMeta) {
        if (!this.authUser) {
            throw new Error('Auth user is required')
        }

        if (!this.authCompanyUser) {
            throw new Error('Auth company user is required')
        }

        return {
            pageMeta: pageMeta,
            appName: this.appName,
            currentRoute: this.currentRoute,
            authUser: this.authUser,
            authCompanyUser: this.authCompanyUser,
            impersonating: this.isImpersonating,
            flashStatus: this.flashStatus,
        }
    }

    getOnboardingLayoutProps() {
        return {
            appName: this.appName,
            flashStatus: this.flashStatus,
        }
    }

    getAuthUser(): User {
        if (!this.authUser) {
            throw new Error('Auth user is required')
        }

        return this.authUser
    }

    getAuthCompanyUser(): CompanyUser {
        if (!this.authCompanyUser) {
            throw new Error('Auth company user is required')
        }

        return this.authCompanyUser
    }

    getAuthCompany(): Company {
        if (!this.authCompany) {
            throw new Error('Auth company is required')
        }

        return this.authCompany
    }

    // ------------------
    // App
    // ------------------

    isSuperAdmin(): boolean {
        return this.getAuthUser().admin
    }
}
